import Phaser from 'phaser';

import { TEXTURE_KEYS } from '@/features/letterParty/game/rendering/assets';
import LottieCanvas, { LOTTIE_TEXTURE_SCALE } from '@/features/letterParty/game/rendering/lottieCanvas';

import djAnimation from '@/features/letterParty/assets/lottie/dj-level-1.json';

import type { LetterPartyTheme } from '@/features/letterParty/types';

const BACKGROUND_WAVES_TOP_OVERLAP = 140;
const MEDIUM_SCREEN_WIDTH = 1024;
const LARGE_SCREEN_WIDTH = 1280;

type AddBoardObject = (object: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[]) => void;

export default class LetterPartyBackgroundRenderer {
	constructor(
		private readonly scene: Phaser.Scene,
		private readonly lottieRenderer: LottieCanvas,
		private readonly addBoardObject: AddBoardObject,
		private readonly theme: LetterPartyTheme,
	) {}

	public render(stageWidth: number, stageHeight: number, score: number, pixelRatio: number) {
		const viewportWidth = stageWidth / pixelRatio;
		const viewportHeight = stageHeight / pixelRatio;
		const background = this.scene.add
			.rectangle(0, 0, stageWidth, stageHeight, this.theme.colors.gameBackground)
			.setOrigin(0)
			.setDepth(-1000);
		this.addBoardObject(background);
		const { palmTree, speaker, waves } = this.theme.animations.gameScreen;

		const wavesTopOverlap = BACKGROUND_WAVES_TOP_OVERLAP * pixelRatio;
		this.lottieRenderer.add({
			key: 'background-waves',
			signature: 'background-waves',
			x: stageWidth / 2,
			y: -wavesTopOverlap,
			width: stageWidth,
			height: Math.max(stageHeight * 0.34, stageWidth * 0.22) + wavesTopOverlap,
			animationData: waves,
			depth: -900,
			originX: 0.5,
			originY: 0,
			isPlaying: true,
		});

		const palmWidth = stageWidth * 0.3;
		this.lottieRenderer.add({
			key: 'background-palm-left',
			signature: 'background-palm-left',
			x: -stageWidth * 0.05,
			y: viewportWidth >= 1024 ? -stageHeight * 0.06 : 0,
			width: palmWidth,
			height: palmWidth,
			animationData: palmTree,
			depth: -800,
			originX: 0,
			originY: 0,
			isPlaying: true,
		});

		if (viewportWidth > viewportHeight) {
			this.lottieRenderer.add({
				key: 'background-palm-right',
				signature: 'background-palm-right',
				x: stageWidth + stageWidth * 0.06,
				y: viewportWidth >= 768 ? 0 : -stageHeight * 0.02,
				width: palmWidth,
				height: palmWidth,
				animationData: palmTree,
				depth: -800,
				originX: 1,
				originY: 0,
				isFlipped: true,
				isPlaying: true,
			});
		}

		this.addImageByWidth(TEXTURE_KEYS.sandLeft, stageWidth * 0.085, stageHeight * 0.68, stageWidth * 0.07, -700);
		this.addImageByWidth(TEXTURE_KEYS.sandRight, stageWidth * 0.94, stageHeight * 0.5, stageWidth * 0.1, -700);
		this.addImageByWidth(TEXTURE_KEYS.sandBottom, stageWidth * 0.5, stageHeight * 0.94, stageWidth * 0.07, -700);
		this.addImageByWidth(TEXTURE_KEYS.sandcastle, stageWidth * 0.115, stageHeight * 0.25, stageWidth * 0.07, -700);

		if (viewportWidth >= 768) {
			this.addImageByHeight(
				TEXTURE_KEYS.swimmingFloat,
				viewportWidth >= 1024 ? stageWidth * 0.15 : stageWidth * 0.05,
				stageHeight * 0.92,
				stageHeight * 0.08,
				-600,
			);
		}

		const djWidth = stageWidth * 0.2;
		const isCompactLandscape = viewportWidth < MEDIUM_SCREEN_WIDTH && viewportWidth > viewportHeight;
		const showDesktopDj = viewportWidth >= LARGE_SCREEN_WIDTH || isCompactLandscape;
		if (showDesktopDj) {
			this.lottieRenderer.add({
				key: 'background-dj',
				signature: 'background-dj',
				x: 0,
				y: stageHeight * 0.95,
				width: djWidth,
				height: djWidth,
				animationData: djAnimation,
				depth: -650,
				originX: 0,
				originY: 1,
				isPlaying: true,
			});
			this.drawDjScore(score, djWidth * 0.5, stageHeight * 0.95 - djWidth * 0.3, djWidth, -650);
		}

		const speakerWidth = stageWidth * 0.2;
		this.lottieRenderer.add({
			key: 'background-speaker',
			signature: 'background-speaker',
			x: stageWidth,
			y: viewportWidth < viewportHeight ? stageHeight * 0.05 : stageHeight,
			width: speakerWidth,
			height: speakerWidth,
			animationData: speaker,
			depth: -700,
			originX: 1,
			originY: viewportWidth < viewportHeight ? 0 : 1,
			isPlaying: true,
		});
	}

	private addImageByWidth(textureKey: string, x: number, y: number, width: number, depth: number) {
		const source = this.scene.textures.get(textureKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
		const imageWidth = source.width || width;
		const imageHeight = source.height || width;
		const height = width * (imageHeight / imageWidth);
		const image = this.scene.add.image(x, y, textureKey).setDisplaySize(width, height).setDepth(depth);

		this.addBoardObject(image);
	}

	private addImageByHeight(textureKey: string, x: number, y: number, height: number, depth: number) {
		const source = this.scene.textures.get(textureKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
		const imageWidth = source.width || height;
		const imageHeight = source.height || height;
		const width = height * (imageWidth / imageHeight);
		const image = this.scene.add.image(x, y, textureKey).setDisplaySize(width, height).setDepth(depth);

		this.addBoardObject(image);
	}

	private drawDjScore(score: number, x: number, y: number, djWidth: number, depth: number) {
		const text = this.scene.add
			.text(x, y, String(score), {
				fontFamily: 'ModernEra, Helvetica, Arial, sans-serif',
				fontSize: `${Math.round(djWidth * 0.19)}px`,
				fontStyle: '900',
				color: '#ffffff',
				align: 'center',
				resolution: LOTTIE_TEXTURE_SCALE,
			})
			.setOrigin(0.5)
			.setDepth(depth);

		this.addBoardObject(text);
	}
}
